/**
 * QC 页全局懒加载：首屏（初始视口内）不处理，其余图片/视频进入视口前再加载。
 * 依赖 lazysizes（lazyload / lazyloaded class）。
 */
(() => {
  const VIDEO_ROOT_MARGIN = '240px 0px';
  const DEBUG = /(?:\?|&)qc_lazy_debug=1(?:&|$)/.test(location.search);
  let initialized = false;
  const initialViewportHeight = window.innerHeight;

  const getSections = () => {
    const main = document.getElementById('MainContent');
    if (!main) return [];
    return Array.from(
      main.querySelectorAll(':scope > .shopify-section, :scope > [id^="shopify-section-"], .shopify-section')
    );
  };

  const getStatus = () => ({
    initialized,
    lazySizes: !!window.lazySizes,
    sections: getSections().length,
    aboveFoldSections: document.querySelectorAll('#MainContent .shopify-section[data-qc-above-fold]').length,
    pending: document.querySelectorAll('#MainContent img.lazyload:not(.lazyloaded)').length,
    loaded: document.querySelectorAll('#MainContent img.lazyloaded').length,
    videos: document.querySelectorAll('#MainContent video[data-qc-lazy-video]').length,
  });

  window.__qcLazyStatus = () => getStatus();

  /** 初始化时已在视口内的 section（含 KV 等首屏内容） */
  const isSectionAboveFold = (section) => {
    const rect = section.getBoundingClientRect();
    return rect.top < initialViewportHeight && rect.bottom > 0;
  };

  const isMediaAboveFold = (el) => {
    const rect = el.getBoundingClientRect();
    return rect.top < initialViewportHeight && rect.bottom > 0;
  };

  const markAboveFoldMedia = (section) => {
    section.querySelectorAll('img').forEach((img) => {
      img.loading = 'eager';
      img.dataset.qcLazyDone = '1';
    });
    section.querySelectorAll('video').forEach((video) => {
      video.dataset.qcLazySkip = '1';
    });
  };

  const shouldSkipMedia = (el) => {
    if (!el || el.dataset.qcLazyDone) return true;
    if (el.dataset.qcLazySkip) return true;
    if (el.closest('[data-qc-lazy-skip]')) return true;
    if (el.closest('[data-qc-above-fold]')) return true;
    if (el.classList.contains('placeholder-svg')) return true;
    if (el.tagName === 'IMG' && el.loading === 'eager') return true;
    if (isMediaAboveFold(el)) return true;
    return false;
  };

  const lazyPicture = (picture) => {
    const img = picture.querySelector('img');
    if (!img || shouldSkipMedia(img)) return;
    if (img.classList.contains('lazyload') && img.dataset.src) {
      img.dataset.qcLazyDone = '1';
      return;
    }

    picture.querySelectorAll('source').forEach((source) => {
      const srcset = source.getAttribute('srcset');
      if (srcset && !source.dataset.srcset) {
        source.dataset.srcset = srcset;
        source.removeAttribute('srcset');
      }
    });

    const src = img.getAttribute('src');
    if (src && !img.dataset.src) {
      img.dataset.src = src;
      img.removeAttribute('src');
    }

    img.loading = 'lazy';
    img.classList.add('lazyload');
    img.dataset.qcLazyDone = '1';

    if (window.lazySizes?.loader) {
      window.lazySizes.loader.unveil(img);
    }
  };

  const lazyImage = (img) => {
    if (shouldSkipMedia(img)) return;
    if (img.closest('picture')) return;
    if (img.classList.contains('lazyload') && img.dataset.src) {
      img.dataset.qcLazyDone = '1';
      return;
    }

    const src = img.getAttribute('src');
    if (src && !img.dataset.src) {
      img.dataset.src = src;
      img.removeAttribute('src');
    }

    img.loading = 'lazy';
    img.classList.add('lazyload');
    img.dataset.qcLazyDone = '1';

    if (window.lazySizes?.loader) {
      window.lazySizes.loader.unveil(img);
    }
  };

  const lazyVideo = (video) => {
    if (!video || video.dataset.qcLazyVideo || video.dataset.qcLazySkip) return;
    video.dataset.qcLazyVideo = '1';

    const shouldAutoplay = video.hasAttribute('autoplay');
    video.preload = 'none';

    if (shouldAutoplay) {
      video.pause();
    }

    const playWhenReady = () => {
      if (!shouldAutoplay) return;
      const p = video.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const v = entry.target;
          if (v.readyState >= 2) {
            playWhenReady();
          } else {
            v.addEventListener('loadeddata', playWhenReady, { once: true });
            if (v.networkState === 0 || v.networkState === 3) v.load();
          }
          observer.unobserve(v);
        });
      },
      { rootMargin: VIDEO_ROOT_MARGIN, threshold: 0.01 }
    );

    observer.observe(video);
  };

  const processSection = (section) => {
    if (section.dataset.qcAboveFold) return;
    section.querySelectorAll('picture').forEach(lazyPicture);
    section.querySelectorAll('img').forEach(lazyImage);
    section.querySelectorAll('video').forEach(lazyVideo);
  };

  const init = () => {
    const sections = getSections();
    if (!sections.length) {
      if (DEBUG) console.warn('[qc-lazyload] 未找到 section，稍后重试');
      return false;
    }

    sections.forEach((section) => {
      if (isSectionAboveFold(section)) {
        section.dataset.qcAboveFold = 'true';
        markAboveFoldMedia(section);
        return;
      }
      processSection(section);
    });

    if (!window.lazySizes) {
      if (DEBUG) console.warn('[qc-lazyload] lazySizes 未就绪');
      return false;
    }

    if (!initialized) {
      const observeNewMedia = () => {
        getSections().forEach((section) => {
          if (section.dataset.qcAboveFold) return;
          processSection(section);
        });
      };

      if (typeof MutationObserver !== 'undefined') {
        const mo = new MutationObserver(() => observeNewMedia());
        mo.observe(document.getElementById('MainContent') || document.body, {
          childList: true,
          subtree: true,
        });
      }
    }

    initialized = true;

    if (DEBUG) {
      console.log('[qc-lazyload] 初始化完成', getStatus());
    }

    return true;
  };

  const boot = () => {
    if (init()) return;
    window.setTimeout(() => init(), 100);
    window.setTimeout(() => init(), 500);
  };

  window.addEventListener('load', boot, { once: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
